import React, { createContext, useState, useContext, useEffect } from 'react';
import { useSettings } from './SettingsContext';
import { useAuth } from './AuthContext';

const CartContext = createContext();
const CART_STORAGE_KEY = 'dulce3d_cart';

export const CartProvider = ({ children }) => {
  const { settings } = useSettings();
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);

  // ✅ Verificar si el usuario es mayorista
  const isWholesale = isAuthenticated && (
    user?.role === 'cliente_mayorista' || 
    user?.is_wholesale === 1 ||
    user?.is_wholesale === true
  );

  useEffect(() => {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setItems(parsed);
      } catch (e) {
        console.error('Error loading cart:', e);
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // ✅ Calcular precio según cantidad y tipo de usuario
  const getItemPrice = (item) => {
    // Si es mayorista Y la cantidad cumple el mínimo Y tiene precio mayorista
    if (
      isWholesale &&
      item.priceWholesale &&
      item.quantity >= (item.minWholesaleQty || 10)
    ) {
      return item.priceWholesale;
    }
    return item.price; // Precio detal
  };

  const addItem = (product) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        return prev.map(i =>
          i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, {
        id: product.id,
        name: product.name,
        price: Number(product.price) || 0,
        priceWholesale: product.priceWholesale || null,
        minWholesaleQty: product.minWholesaleQty || 10,
        icon: product.icon || '🍞',
        quantity: 1
      }];
    });
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  // ===== CÁLCULOS =====
  const totalItems = items.reduce((sum, i) => sum + (i.quantity || 0), 0);

  // Calcular subtotal usando precios que aplican
  const subtotalUSD = items.reduce((sum, i) => {
    const price = getItemPrice(i);
    return sum + (price * i.quantity);
  }, 0);

  const exchangeRate = Number(settings?.exchange_rate) || 40;
  const subtotalBS = subtotalUSD * exchangeRate;
  const deliveryFeeUSD = Number(settings?.delivery_fee_usd) || 0;
  const deliveryFeeBS = deliveryFeeUSD * exchangeRate;
  const totalUSD = subtotalUSD + deliveryFeeUSD;
  const totalBS = subtotalBS + deliveryFeeBS;

  // Verificar si algún item tiene precio mayorista aplicado
  const hasWholesaleItems = items.some(i => 
    isWholesale && i.priceWholesale && i.quantity >= (i.minWholesaleQty || 10)
  );

  return (
    <CartContext.Provider value={{
      items,
      totalItems,
      subtotalUSD,
      subtotalBS,
      deliveryFeeUSD,
      deliveryFeeBS,
      totalUSD,
      totalBS,
      exchangeRate,
      isWholesale,
      hasWholesaleItems,
      getItemPrice,
      addItem,
      removeItem,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};
